package com.wintermar.inspection.core.ui.component.image.util

import io.github.vinceglb.filekit.PlatformFile
import io.github.vinceglb.filekit.readBytes
import io.github.vinceglb.filekit.size
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.flow.flowOn
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.khronos.webgl.Uint8Array
import org.w3c.files.Blob
import kotlin.js.Promise
import kotlin.math.ceil


@JsModule("browser-image-compression")
@JsNonModule
external object ImageCompression {
    @JsName("default")
    fun compress(file: Blob, options: dynamic): Promise<Blob>
}

@JsModule("jszip")
@JsNonModule
external object JSZip {
    @JsName("default")
    class JSZipInstance {
        fun file(name: String, data: Uint8Array)
        fun generateAsync(options: dynamic): Promise<Blob>
    }

    fun build(): JSZipInstance
}

actual suspend fun compress(
    file: PlatformFile,
    targetSizeKb: Int
): ByteArray {
    val options = js("{}")
    options.maxSizeMB = targetSizeKb / 1024.0
    options.maxWidthOrHeight = 1500
    options.useWebWorker = true

    val bytes = file.readBytes()
    val uint8Array = Uint8Array(bytes.unsafeCast<Int8Array>().buffer)
    val inputBlob = Blob(arrayOf(uint8Array), js("{ type: 'image/jpeg' }"))
    val compressedBlob = ImageCompression.compress(inputBlob, options).await()

    val uint8Promise = compressedBlob.asDynamic().arrayBuffer() as Promise<ArrayBuffer>
    val buffer = uint8Promise.await()
    // Create a real Int8Array view so that Kotlin's isByteArray() check passes in Coil.
    // A plain unsafeCast of Uint8Array → ByteArray leaves a Uint8Array at runtime, which
    // fails the `a instanceof Int8Array` check inside Coil's ByteArrayFetcher factory.
    val int8 = Int8Array(buffer)
    return int8.unsafeCast<ByteArray>()
}

actual suspend fun deleteTemporaryFile(file: PlatformFile) {
    // Browser manages cache automatically, no-op needed
}

actual fun streamChunkFiles(
    file: FileModel,
    targetSizeKb: Int
): Flow<FileChunkModel> = flow {
    val chunkSizeLimit = targetSizeKb * 1024

    // 1. Pull full byte profile safely (FileKit optimizes this on JS)
    val fileBytes = file.blob ?: file.platformFile?.readBytes() ?: return@flow
    val fileSize = fileBytes.size

    // Exact ceiling math matching the UseCase's completion threshold
    val totalChunks = if (fileSize > 0) {
        ceil(fileSize.toDouble() / chunkSizeLimit.toDouble()).toInt()
    } else {
        1
    }

    var start = 0
    var chunkIndex = 0

    // 2. Perform zero-copy index slicing sequentially
    while (start < fileSize) {
        val end = minOf(start + chunkSizeLimit, fileSize)

        // This cuts perfectly sized byte chunks safely on the JS side
        val chunkBytes = fileBytes.sliceArray(start until end)

        emit(FileUtil.createChunk(file, chunkIndex, chunkBytes, fileSize, totalChunks))

        start = end
        chunkIndex++
    }
}.flowOn(Dispatchers.Default)
